import { z } from "zod";

import type { CatalogOrder } from "./catalog-order";

/**
 * The catalog query, in query-string form — the add scope's whole state.
 *
 * One module for both the client that writes it and the BFF route that reads it, so the
 * parameter names exist once. They are in Portuguese, like the statement's (`entries-query.ts`),
 * because a query string is an address a person can read and share.
 *
 * The shape follows the filters spec §9 (`Filtros do Catalogo - handoff.md`), narrowed to what
 * this product's catalog actually carries: the `estado` axis belongs to the internal area, and
 * the navigation trail (departamento/categoria/tipo) waits for the canonical taxonomy the same
 * spec asks the backend for (§9, pendências D-03 and D-04).
 */

/**
 * Page sizes for the product grid. Canon §7 writes 20 · 25 · 50 for tables; on a grid of 3 columns
 * (2 between 641 and 1024) those leave the last row short. The person ruled on 2026-09-29 for
 * multiples of six from canon §7's floor of twenty: every row full, at 3 columns and at 2.
 */
export const CATALOG_PAGE_SIZES = [24, 48, 96] as const;

export type CatalogPageSize = (typeof CATALOG_PAGE_SIZES)[number];

export const CATALOG_DEFAULT_PAGE_SIZE: CatalogPageSize = 24;

/** The endpoint's own ceiling, and the reason a page size is a closed set here. */
export const CATALOG_MAX_PAGE_SIZE = 100;

/** §3: a term is one line; the backend matches it against the name **or** the SKU. */
export const CATALOG_SEARCH_MAX_LENGTH = 100;

export const CATALOG_PARAM = {
  search: "busca",
  category: "categoria",
  trailType: "tipoTrilho",
  collection: "colecao",
  type: "tipo",
  material: "material",
  priceMin: "precoMin",
  priceMax: "precoMax",
  order: "ordem",
  page: "pagina",
  pageSize: "porPagina",
} as const;

export type CatalogQuery = {
  /** The **applied** term (§3: typing does not filter; submitting does). */
  search: string;
  /**
   * Category id, as the backend's `categories` aggregate returns it — the navigation trail's
   * second level (handoff §4). **One** value: the endpoint takes a singular `categoryId` and
   * answers 400 to `categoryIds` and `category` (measured 2026-10-14). Navigation, not filter:
   * `hasCatalogFilters` leaves it out, so "Limpar filtros" keeps it.
   */
  categoryId: string | null;
  /**
   * The type chosen in the navigation trail's third line (N3, handoff §2 item 6), as the
   * `tipo-de-produto` attribute's value. Navigation, so `hasCatalogFilters` leaves it out.
   *
   * It travels as one more `attribute=tipo-de-produto:<value>` — the same parameter the "Tipo"
   * facet's marks use, and the backend **ORs** repeated values of one slug. They are therefore
   * never sent together: choosing a type in the trail clears the marks and the facet then holds
   * only that type, which is below its two-value minimum and does not draw (`facetTypesInScope`).
   */
  trailType: string | null;
  /** Collection id, as the backend's facet returns it. */
  collectionId: string | null;
  /** Attribute values, OR within the axis and AND across axes (§5). */
  types: readonly string[];
  materials: readonly string[];
  /** Bounds in integer cents, over the pack's total — the only money the panel speaks. */
  priceMinCents: number | null;
  priceMaxCents: number | null;
  order: CatalogOrder;
  page: number;
  pageSize: CatalogPageSize;
};

export const EMPTY_CATALOG_QUERY: CatalogQuery = {
  search: "",
  categoryId: null,
  trailType: null,
  collectionId: null,
  types: [],
  materials: [],
  priceMinCents: null,
  priceMaxCents: null,
  order: "curadoria",
  page: 1,
  pageSize: CATALOG_DEFAULT_PAGE_SIZE,
};

/**
 * Out-of-domain values fall back instead of failing the route: a query string is hand-editable,
 * and a stale address is not an attack. What keeps arbitrary input out is that every set is
 * closed and every string is bounded.
 */
const orderSchema = z
  .enum(["curadoria", "menor", "maior", "az", "za", "presenteados"])
  .catch("curadoria")
  // "Mais presenteados" reads the host's own numbers, which a catalog product has none of.
  .transform((order): CatalogOrder => (order === "presenteados" ? "curadoria" : order));

const pageSchema = z
  .string()
  .regex(/^\d{1,6}$/u)
  .transform(Number)
  .pipe(z.number().int().min(1))
  .catch(1);

const pageSizeSchema = z
  .string()
  .regex(/^\d{1,3}$/u)
  .transform(Number)
  .pipe(z.union([z.literal(24), z.literal(48), z.literal(96)]))
  .catch(CATALOG_DEFAULT_PAGE_SIZE);

const centsSchema = z
  .string()
  .regex(/^\d{1,9}$/u)
  .transform(Number)
  .pipe(z.number().int().nonnegative())
  .nullable()
  .catch(null);

const valueSchema = z.string().trim().min(1).max(80);

export function parseCatalogQuery(params: URLSearchParams): CatalogQuery {
  const single = (name: string) => params.get(name) ?? "";
  const many = (name: string) =>
    params
      .getAll(name)
      .map((value) => valueSchema.safeParse(value))
      .flatMap((parsed) => (parsed.success ? [parsed.data] : []))
      // A repeated value would send the same filter twice to the backend.
      .filter((value, index, all) => all.indexOf(value) === index)
      .slice(0, 20);

  const collection = valueSchema.safeParse(single(CATALOG_PARAM.collection));
  // An id the backend would reject must not travel: the endpoint answers 400 to a malformed uuid.
  const category = z.string().uuid().safeParse(single(CATALOG_PARAM.category));
  const trailType = valueSchema.safeParse(single(CATALOG_PARAM.trailType));

  return {
    search: single(CATALOG_PARAM.search).trim().slice(0, CATALOG_SEARCH_MAX_LENGTH),
    categoryId: category.success ? category.data : null,
    // Only meaningful inside a category (N3 does not exist outside one).
    trailType: category.success && trailType.success ? trailType.data : null,
    collectionId: collection.success ? collection.data : null,
    types: many(CATALOG_PARAM.type),
    materials: many(CATALOG_PARAM.material),
    priceMinCents: centsSchema.parse(params.get(CATALOG_PARAM.priceMin)),
    priceMaxCents: centsSchema.parse(params.get(CATALOG_PARAM.priceMax)),
    order: orderSchema.parse(single(CATALOG_PARAM.order)),
    page: pageSchema.parse(single(CATALOG_PARAM.page)),
    pageSize: pageSizeSchema.parse(single(CATALOG_PARAM.pageSize)),
  };
}

/** The way back: what the client sends, and what a person can keep in the address bar. */
export function catalogQueryToSearchParams(query: CatalogQuery): URLSearchParams {
  const params = new URLSearchParams();

  if (query.search !== "") params.set(CATALOG_PARAM.search, query.search);
  if (query.categoryId !== null) params.set(CATALOG_PARAM.category, query.categoryId);
  if (query.trailType !== null) params.set(CATALOG_PARAM.trailType, query.trailType);
  if (query.collectionId !== null) params.set(CATALOG_PARAM.collection, query.collectionId);
  for (const type of query.types) params.append(CATALOG_PARAM.type, type);
  for (const material of query.materials) params.append(CATALOG_PARAM.material, material);
  if (query.priceMinCents !== null) params.set(CATALOG_PARAM.priceMin, String(query.priceMinCents));
  if (query.priceMaxCents !== null) params.set(CATALOG_PARAM.priceMax, String(query.priceMaxCents));
  if (query.order !== "curadoria") params.set(CATALOG_PARAM.order, query.order);
  if (query.page !== 1) params.set(CATALOG_PARAM.page, String(query.page));
  if (query.pageSize !== CATALOG_DEFAULT_PAGE_SIZE) params.set(CATALOG_PARAM.pageSize, String(query.pageSize));

  return params;
}

/**
 * Whether anything narrows the catalog — what "Limpar filtros" turns off (§8). The category is
 * navigation (handoff §2, reset table) and is deliberately not counted here.
 */
export function hasCatalogFilters(query: CatalogQuery): boolean {
  return (
    query.search !== "" ||
    query.collectionId !== null ||
    query.types.length > 0 ||
    query.materials.length > 0 ||
    query.priceMinCents !== null ||
    query.priceMaxCents !== null
  );
}
